// Which drawer or modal the `modal` descriptor maps onto. Every flow that opens
// another one does so by replacing that descriptor, so the transitions between
// them live here rather than scattered through the page.

import React from 'react';
import PropTypes from 'prop-types';

import CreateEditDrawer from './CreateEditDrawer';
import {
  DeleteModal,
  ImportModal,
  LockModal,
  MigrateModal,
} from './ActionModals';
import VersionDiffModal from './VersionDiffModal';
import { currentVersion } from './atUtils';

// Opening Migrate from anywhere needs the same four fields, and the source
// version's current integrations.
const migrateDescriptor = (chain, fromVersion, toVersion) => {
  const fromRef = chain.versions.find((v) => v.version === fromVersion);
  return {
    kind: 'migrate',
    actionId: chain.id,
    fromVersion,
    toVersion,
    integrationsOnFrom: fromRef?.integrations || [],
  };
};

export default function ActionModalRouter({
  modal = null,
  setModal,
  actions,
  action = null,
  version = null,
  crud,
}) {
  const close = () => setModal(null);

  if (modal?.kind === 'create') {
    return (
      <CreateEditDrawer
        mode="create"
        allActions={actions}
        onClose={close}
        onSave={crud.createAction}
      />
    );
  }

  if (modal?.kind === 'import') {
    return <ImportModal onClose={close} onImport={crud.handleImport} />;
  }

  if (modal?.kind === 'edit' && action && version) {
    return (
      <CreateEditDrawer
        mode="edit"
        action={action}
        version={version}
        allActions={actions}
        onClose={close}
        onSave={({ mode: m, data }) =>
          m === 'new-version'
            ? crud.createNewVersion({ data })
            : crud.editInPlace({ data })
        }
      />
    );
  }

  if (modal?.kind === 'migrate') {
    const chain = actions.find((x) => x.id === modal.actionId);
    if (!chain) return null;

    const fromV = chain.versions.find((v) => v.version === modal.fromVersion);
    const toV = chain.versions.find((v) => v.version === modal.toVersion);
    if (!fromV || !toV) return null;

    return (
      <MigrateModal
        action={chain}
        fromVersion={fromV}
        toVersion={toV}
        integrationsOnFrom={modal.integrationsOnFrom}
        onClose={close}
        onMigrate={(ints) =>
          crud.performMigrate(
            modal.actionId,
            modal.fromVersion,
            modal.toVersion,
            ints,
          )
        }
        // Comparing from inside Migrate remembers where to come back to.
        onCompare={() =>
          setModal({
            kind: 'diff',
            actionId: modal.actionId,
            fromVersion: modal.fromVersion,
            toVersion: modal.toVersion,
            returnTo: { ...modal },
          })
        }
      />
    );
  }

  if (modal?.kind === 'diff') {
    const chain = actions.find((x) => x.id === modal.actionId);
    if (!chain) return null;

    return (
      <VersionDiffModal
        action={chain}
        fromVersion={modal.fromVersion}
        toVersion={modal.toVersion}
        onClose={() => setModal(modal.returnTo || null)}
        // Arriving here from Migrate, the migrate action is already behind us.
        onMigrate={
          modal.returnTo
            ? null
            : (fromV, toV) => setModal(migrateDescriptor(chain, fromV, toV))
        }
      />
    );
  }

  if (modal?.kind === 'delete' && action && version) {
    return (
      <DeleteModal
        action={action}
        version={version}
        onClose={close}
        onDelete={() => crud.deleteVersion()}
        onMigrateInstead={(fromV) =>
          setModal(
            migrateDescriptor(action, fromV, currentVersion(action).version),
          )
        }
      />
    );
  }

  if (modal?.kind === 'lock' && action && version) {
    return (
      <LockModal
        action={action}
        version={version}
        onClose={close}
        onConfirm={crud.toggleLock}
      />
    );
  }

  return null;
}

ActionModalRouter.propTypes = {
  modal: PropTypes.oneOfType([PropTypes.object]),
  setModal: PropTypes.func.isRequired,
  actions: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object]))
    .isRequired,
  action: PropTypes.oneOfType([PropTypes.object]),
  version: PropTypes.oneOfType([PropTypes.object]),
  crud: PropTypes.oneOfType([PropTypes.object]).isRequired,
};
